import Swiper from 'swiper'
import { Navigation, Pagination, A11y, Mousewheel, FreeMode } from 'swiper/modules'

/**
 * Initialize a simple, elegant swiper instance for product carousels
 * with smooth navigation and touch support
 * @returns {Object} Alpine.js component methods
 */
export default () => {
  return {
    swiper: null,
    initialized: false,
    blockId: null,
    step: 1,
    
    /**
     * Initialize the swiper instance
     */
    init() {
      // Wait for Alpine to fully initialize
      this.$nextTick(() => {
        this.blockId = this.$el.querySelector('[id^="Slider-"]')?.id
        if (!this.blockId) return
        
        // Get the step size from buttons if available
        const nextButton = this.$el.querySelector('button[name="next"]')
        if (nextButton && nextButton.dataset.step) {
          this.step = parseInt(nextButton.dataset.step, 10) || 1
        }
        
        // Find the existing navigation buttons
        const prevButton = this.$el.querySelector('button[name="previous"]')
        const nextButton2 = this.$el.querySelector('button[name="next"]')
        
        // Get the slider element
        const sliderEl = document.getElementById(this.blockId)
        if (!sliderEl) return
        
        // Create a clean Swiper implementation (not modifying the original DOM)
        this.createSwiper(sliderEl, prevButton, nextButton2)
        
        // Re-init on resize for responsive behavior
        window.addEventListener('resize', this.handleResize.bind(this))
      })
    },
    
    /**
     * Create a swiper instance with the product slides
     */
    createSwiper(sliderEl, prevButton, nextButton) {
      if (this.initialized) return
      
      // Hide the original slider
      sliderEl.style.display = 'none'
      
      // Create swiper container
      const swiperContainer = document.createElement('div')
      swiperContainer.classList.add('swiper', 'product-swiper')
      swiperContainer.id = `Swiper-${this.blockId}`
      
      // Create swiper wrapper
      const swiperWrapper = document.createElement('div')
      swiperWrapper.classList.add('swiper-wrapper')
      swiperContainer.appendChild(swiperWrapper)
      
      // Copy slides from the original slider
      const originalSlides = Array.from(sliderEl.children)
      originalSlides.forEach(originalSlide => {
        const swiperSlide = document.createElement('div')
        swiperSlide.classList.add('swiper-slide')
        
        // Clone the original slide with all content and event listeners
        const clone = originalSlide.cloneNode(true)
        
        // Remove height restrictions and add appropriate class
        clone.style.height = 'auto'
        clone.classList.remove('slider__slide')
        clone.classList.add('product-slide')
        
        // Allow natural dimensions of product cards
        const card = clone.querySelector('.card')
        if (card) {
          card.style.height = 'auto'
        }
        
        // Add the clone to our swiper slide
        swiperSlide.appendChild(clone)
        swiperWrapper.appendChild(swiperSlide)
      })
      
      // Add pagination for mobile
      const pagination = document.createElement('div')
      pagination.classList.add('swiper-pagination')
      swiperContainer.appendChild(pagination)
      
      // Insert swiper after the original slider
      sliderEl.parentNode.insertBefore(swiperContainer, sliderEl.nextSibling)
      
      // Calculate responsive slide widths
      const slideWidth = originalSlides[0]?.getBoundingClientRect().width || 289 // Default width from settings
      const containerWidth = sliderEl.parentNode.getBoundingClientRect().width || 1200
      const visibleSlides = Math.floor(containerWidth / (slideWidth + 6)) // 6px is the spacing
      
      // Set a smaller step for smoother scrolling
      const effectiveStep = Math.min(this.step, Math.max(1, Math.floor(visibleSlides / 2)))
      
      // Set a dynamic height for the container based on tallest card
      let maxCardHeight = 0
      originalSlides.forEach(slide => {
        const slideHeight = slide.getBoundingClientRect().height
        maxCardHeight = Math.max(maxCardHeight, slideHeight)
      })
      
      // Set minimum height with some buffer
      if (maxCardHeight > 0) {
        swiperContainer.style.minHeight = (maxCardHeight + 20) + 'px'
      }
      
      // Initialize Swiper with simple configuration
      this.swiper = new Swiper(swiperContainer, {
        modules: [Navigation, Pagination, A11y, Mousewheel, FreeMode],
        slidesPerView: 'auto',
        spaceBetween: 6,
        grabCursor: true,
        speed: 600, // Smoother transitions
        cssMode: false, // Disable for smoother animations
        autoHeight: true, // Allow dynamic heights
        freeMode: {
          enabled: true,
          momentum: true,
          momentumRatio: 0.4,
          momentumBounce: true,
          minimumVelocity: 0.1,
          sticky: true,
        },
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
          disabledClass: 'opacity-50',
        },
        pagination: {
          el: pagination,
          clickable: true,
          dynamicBullets: true,
          dynamicMainBullets: 3
        },
        slidesPerGroup: effectiveStep,
        watchOverflow: true,
        observer: true, // Better handling of dynamic content
        observeParents: true,
        resizeObserver: true,
        slideToClickedSlide: false,
        touchReleaseOnEdges: true,
        preventInteractionOnTransition: false,
        touchRatio: 1,
        touchAngle: 45,
        simulateTouch: true,
        mousewheel: {
          enabled: true,
          sensitivity: 1,
          forceToAxis: true,
          releaseOnEdges: true,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous products',
          nextSlideMessage: 'Next products',
        },
        breakpoints: {
          320: { 
            slidesPerView: 'auto',
            spaceBetween: 6,
            freeMode: {
              enabled: true,
              momentumRatio: 0.5, // Higher momentum for mobile
            }
          },
          768: { 
            slidesPerView: 'auto',
            spaceBetween: 6,
            freeMode: {
              enabled: true,
              momentumRatio: 0.4,
            }
          },
          1024: { 
            slidesPerView: 'auto',
            spaceBetween: 6,
            freeMode: {
              enabled: true,
              momentumRatio: 0.3, // Lower momentum for desktop precision
            }
          }
        }
      })
      
      // Add event listeners for smooth button handling
      if (prevButton) {
        prevButton.addEventListener('click', (e) => {
          e.preventDefault()
          this.swiper.slidePrev()
        })
      }
      
      if (nextButton) {
        nextButton.addEventListener('click', (e) => {
          e.preventDefault()
          this.swiper.slideNext()
        })
      }
      
      // Add special handling for trackpad gestures
      swiperContainer.addEventListener('wheel', (e) => {
        // Only handle horizontal scrolling with trackpad
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 10) {
          e.preventDefault()
          // Determine direction and slide
          if (e.deltaX > 0) {
            this.swiper.slideNext()
          } else {
            this.swiper.slidePrev()
          }
        }
      }, { passive: false })
      
      this.initialized = true
    },
    
    /**
     * Handle window resize events
     */
    handleResize() {
      if (this.swiper) {
        this.swiper.update()
      }
    },
    
    /**
     * Destroy the swiper instance
     */
    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true)
        this.swiper = null
        this.initialized = false
        
        // Show the original slider again
        const sliderEl = document.getElementById(this.blockId)
        if (sliderEl) {
          sliderEl.style.display = ''
        }
        
        // Remove the swiper container
        const swiperContainer = document.getElementById(`Swiper-${this.blockId}`)
        if (swiperContainer) {
          swiperContainer.remove()
        }
      }
    }
  }
}